import { PermissionInputRow, PermissionRow, PolicyInputRow, PolicyRow, RoleInputRow, RoleRow } from "namirasoft-access";
import { IEntityInfo, NSARouterMakerResult } from "namirasoft-account-react";
import { ProductUUID } from "namirasoft-api-product";
import { IStorageLocal, NamingConvention } from "namirasoft-core";
import { NamirasoftMap } from "namirasoft-site-map";
import { BaseColumnFormatter, BooleanFormatter, DateTimeFormatter, EmailFormatter, ForeColorFormatter, IDFormatter, StringFormatter, StringFormatterSizeType } from "namirasoft-site-react";
import { AccessMenus } from "./AccessMenus";
import { AccessProps } from "./AccessProps";
import { AccessRoutes } from "./AccessRoutes";

export class AccessEntities 
{
	static policy(props: NSARouterMakerResult & AccessProps): IEntityInfo<PolicyRow, PolicyInputRow>
	{
		let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

		return {
			product_id: ProductUUID.NAMIRASOFT_ACCESS,
			name: "policy",
			tables: [{ table: namirasoft.access.tables.policy, required: true }],
			client: {
				getListURL: () => AccessRoutes.PolicyList,
				getNewURL: () => AccessRoutes.PolicyNew,
				getCopyURL: (id: string) => AccessRoutes.PolicyCopy(id),
				getEditURL: (id: string) => AccessRoutes.PolicyEdit(id),
				getViewURL: (id: string) => AccessRoutes.PolicyView(id),
				getColumns: () =>
				{
					return [
						BaseColumnFormatter.getColumn(namirasoft.access.tables.policy.columns.id, new IDFormatter(namirasoft, null)),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.policy.columns.user_id, new IDFormatter(namirasoft, new IStorageLocal())),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.policy.columns.product_id, new IDFormatter(namirasoft, new IStorageLocal())),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.policy.columns.name, new StringFormatter(StringFormatterSizeType.FullName)),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.policy.columns.entity, new StringFormatter(StringFormatterSizeType.TwoWords, NamingConvention.Pascal_Case_Space)),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.policy.columns.actions, new StringFormatter(StringFormatterSizeType.FullName)),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.policy.columns.resources, new StringFormatter(StringFormatterSizeType.Word)),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.policy.columns.allow, new ForeColorFormatter(new BooleanFormatter("Allow", "Deny"), { true: "green", false: "red" })),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.policy.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.policy.columns.created_at, new DateTimeFormatter()),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.policy.columns.updated_at, new DateTimeFormatter())
					];
				},
				getQuickFilters: () => [{ column: "user_id", operator: "exists", title: "User Defined Policies" }]
			},
			server: {
				value_list: props.servers.access.value.List,
				get: props.servers.access.policy.Get,
				list: props.servers.access.policy.List,
				create: props.servers.access.policy.Create,
				update: props.servers.access.policy.Update,
				delete: props.servers.access.policy.Delete,
			}
		};
	}
	static role(props: NSARouterMakerResult & AccessProps): IEntityInfo<RoleRow, RoleInputRow>
	{
		let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

		return {
			product_id: ProductUUID.NAMIRASOFT_ACCESS,
			name: "role",
			tables: [{ table: namirasoft.access.tables.role, required: true }],
			client: {
				getListURL: () => AccessRoutes.RoleList,
				getNewURL: () => AccessRoutes.RoleNew,
				getCopyURL: (id: string) => AccessRoutes.RoleCopy(id),
				getEditURL: (id: string) => AccessRoutes.RoleEdit(id),
				getViewURL: (id: string) => AccessRoutes.RoleView(id),
				getColumns: () =>
				{
					return [
						BaseColumnFormatter.getColumn(namirasoft.access.tables.role.columns.id, new IDFormatter(namirasoft, null)),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.role.columns.user_id, new IDFormatter(namirasoft, new IStorageLocal())),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.role.columns.name, new StringFormatter(StringFormatterSizeType.FullName)),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.role.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.role.columns.created_at, new DateTimeFormatter()),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.role.columns.updated_at, new DateTimeFormatter())
					];
				},
				getQuickFilters: () => [{ column: "user_id", operator: "exists", title: "User Defined Roles" }]
			},
			server: {
				value_list: props.servers.access.value.List,
				get: props.servers.access.role.Get,
				list: props.servers.access.role.List,
				create: props.servers.access.role.Create,
				update: props.servers.access.role.Update,
				delete: props.servers.access.role.Delete,
			}
		};
	}
	static incomingPermission(props: NSARouterMakerResult & AccessProps): IEntityInfo<PermissionRow, PermissionInputRow>
	{
		let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

		return {
			product_id: ProductUUID.NAMIRASOFT_ACCESS,
			name: "permission",
			tables: [{ table: namirasoft.access.tables.permission, required: true }],
			client: {
				getListURL: () => AccessRoutes.IncomingPermissionList,
				getNewURL: null,
				getCopyURL: null,
				getEditURL: null,
				getViewURL: (id: string) => AccessRoutes.IncomingPermissionView(id),
				getColumns: () =>
				{
					return [
						BaseColumnFormatter.getColumn(namirasoft.access.tables.permission.columns.id, new IDFormatter(namirasoft, null)),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.permission.columns.user_id, new IDFormatter(namirasoft, new IStorageLocal())),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.permission.columns.from_email, new EmailFormatter()),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.permission.columns.from_first_name, new StringFormatter(StringFormatterSizeType.TwoWords)),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.permission.columns.from_last_name, new StringFormatter(StringFormatterSizeType.TwoWords)),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.permission.columns.to_user_id, new IDFormatter(namirasoft, new IStorageLocal())),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.permission.columns.to_email, new EmailFormatter()),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.permission.columns.to_first_name, new StringFormatter(StringFormatterSizeType.TwoWords)),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.permission.columns.to_last_name, new StringFormatter(StringFormatterSizeType.TwoWords)),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.permission.columns.created_at, new DateTimeFormatter()),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.permission.columns.updated_at, new DateTimeFormatter())
					];
				},
				getMenus: (getIDs, getRows) =>
				{
					return {
						"Switch": [AccessMenus.switchToMyOwn(props), AccessMenus.switchToUser(props, getIDs, getRows)]
					};
				},
			},
			server: {
				value_list: props.servers.access.value.List,
				get: props.servers.access.permission.GetIncoming,
				list: props.servers.access.permission.ListIncoming,
				create: props.servers.access.permission.Create,
				update: props.servers.access.permission.Update,
				delete: props.servers.access.permission.DeleteIncoming,
			}
		};
	}
	static outgoingPermission(props: NSARouterMakerResult & AccessProps): IEntityInfo<PermissionRow, PermissionInputRow>
	{
		let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

		return {
			product_id: ProductUUID.NAMIRASOFT_ACCESS,
			name: "permission",
			tables: [{ table: namirasoft.access.tables.permission, required: true }],
			client: {
				getListURL: () => AccessRoutes.OutgoingPermissionList,
				getNewURL: () => AccessRoutes.OutgoingPermissionNew,
				getCopyURL: (id: string) => AccessRoutes.OutgoingPermissionCopy(id),
				getEditURL: (id: string) => AccessRoutes.OutgoingPermissionEdit(id),
				getViewURL: (id: string) => AccessRoutes.OutgoingPermissionView(id),
				getColumns: () =>
				{
					return [
						BaseColumnFormatter.getColumn(namirasoft.access.tables.permission.columns.id, new IDFormatter(namirasoft, null)),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.permission.columns.user_id, new IDFormatter(namirasoft, new IStorageLocal())),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.permission.columns.from_email, new EmailFormatter()),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.permission.columns.from_first_name, new StringFormatter(StringFormatterSizeType.TwoWords)),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.permission.columns.from_last_name, new StringFormatter(StringFormatterSizeType.TwoWords)),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.permission.columns.to_user_id, new IDFormatter(namirasoft, new IStorageLocal())),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.permission.columns.to_email, new EmailFormatter()),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.permission.columns.to_first_name, new StringFormatter(StringFormatterSizeType.TwoWords)),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.permission.columns.to_last_name, new StringFormatter(StringFormatterSizeType.TwoWords)),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.permission.columns.created_at, new DateTimeFormatter()),
						BaseColumnFormatter.getColumn(namirasoft.access.tables.permission.columns.updated_at, new DateTimeFormatter())
					];
				}
			},
			server: {
				value_list: props.servers.access.value.List,
				get: props.servers.access.permission.GetOutgoing,
				list: props.servers.access.permission.ListOutgoing,
				create: props.servers.access.permission.Create,
				update: props.servers.access.permission.Update,
				delete: props.servers.access.permission.DeleteOutgoing,
			}
		};
	}
}